/**
 * UUID generation that works outside a secure context.
 *
 * `crypto.randomUUID()` is only defined in SECURE CONTEXTS — HTTPS, or
 * `localhost`. Open the same dev server by LAN IP (`http://192.168.x.x:5173`,
 * which is how you test from a second machine or a phone) and `crypto` still
 * exists but `randomUUID` is `undefined`, so every call site throws
 * `crypto.randomUUID is not a function` and the app dies at import time.
 *
 * That is not a hypothetical: it took down the whole web client during local
 * design testing. The value is a client-side correlation id — a chat id, a
 * guest id, an upload id — so a non-cryptographic fallback is fine. What is NOT
 * fine is the app refusing to boot.
 *
 * Order of preference:
 *   1. `crypto.randomUUID()`      — secure context, real UUID v4
 *   2. `crypto.getRandomValues()` — available over plain HTTP; still CSPRNG,
 *                                   just hand-assembled into UUID v4 shape
 *   3. `Math.random()`            — last resort, e.g. very old WebViews
 */

type MaybeCrypto = {
  randomUUID?: () => string;
  getRandomValues?: <T extends ArrayBufferView>(array: T) => T;
};

function getCrypto(): MaybeCrypto | undefined {
  return typeof globalThis !== 'undefined'
    ? (globalThis.crypto as MaybeCrypto | undefined)
    : undefined;
}

/** Assemble a v4 UUID from 16 random bytes, setting the version/variant bits. */
function uuidFromBytes(bytes: Uint8Array): string {
  // Version 4 and RFC-4122 variant — without these the string looks like a UUID
  // but is not one, and anything that validates the shape would reject it.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < 16; i += 1) hex.push((bytes[i] ?? 0).toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

export function randomUUID(): string {
  const c = getCrypto();

  if (typeof c?.randomUUID === 'function') {
    return c.randomUUID();
  }

  if (typeof c?.getRandomValues === 'function') {
    return uuidFromBytes(c.getRandomValues(new Uint8Array(16)));
  }

  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return uuidFromBytes(bytes);
}
